<script lang="ts">

    import generatedID from "../utils/generatedID";
    import type {ServerProduct} from "../@types/product";
    import type {Specs, WidgetProps} from "../@types/recommendation";
    import setProducts from "../store/actions/setProducts";
    import {
        widgetWidth,
        widgetHeight,
        products,
        previewMode,
        Reporting,
        platform,
        ReportingProductsShownSlice
    } from "../store/store";
    import Product from "./Product.svelte";
    import DotsContainer from "./DotsContainer.svelte";
    import LeftArrowIcon from "../icons/LeftArrowIcon.svelte";
    import RightArrowIcon from "../icons/RightArrowIcon.svelte";
    import Spinner from "../icons/Spinner.svelte";
    import {containerWidth} from "../utils/containerWidth";
    import {containerHeight} from "../utils/containerHeight";
    import {ImgRatio, Source} from "../@types/recommendation";
    import {swipe} from "../utils/gestures/swipeGesture";
    import {getImageSize} from "../utils/getImageParams";
    import useCustomCSS from "../hooks/useCustomCSS";
    import {Hooks, CustomizationEvent} from "../utils/registerHook";
    import hook from "../directives/hook";
    import setPreviewProducts from "../store/actions/setPreviewProducts";
    import {getProductPageID} from "@fast-simon/common";
    import {createGTagEventObj} from "../utils/createGTagEventObj";
    import getUserSession from "../utils/getUserSession";


    /* Props */
    export let imgRatio: ImgRatio;
    export let layout: string;
    export let maxSuggest: number;
    export let title: string;
    export let sources: Source[];
    export let productsPerPage: number;
    export let productsPerMobile: number;
    export let currentDevice: string;
    export let target: HTMLElement | ShadowRoot;
    export let collections: string[];
    export let productsIds: string;
    export let fixedImageSize: string;
    export let onHoverCarousel: boolean;
    export let dev: boolean;
    export let addToCart: boolean;

    const id = generatedID("fast-simon-recommendations");

    export let specs: Specs = {
        sources: sources,
        maxSuggestions: maxSuggest,
        widgetID: id,
        title: title,
        categoriesIDs: collections,
    }

    export let props: WidgetProps = {
        specs: specs,
    }


    /* State */
    let productsList: ServerProduct[] = [];
    let loading: boolean = true;
    let transformPX: number = 0
    let numOfMoves: number = 0
    let dotsLength: number = 1;
    let currentDot: number = 0;
    let currentPage: number = 0;
    let carouselContainerWidth: number = 0;
    let carouselContainerHeight: number = 0;
    let carouselContainerStyle: string = "";
    let numOfProductsDisplay: number = 0;
    let productsWithReviews: any = false;

    const productsViewedPostLoad = () => {
        let alreadyShownSlices = $ReportingProductsShownSlice;
        if(productsList) {
            let s = numOfMoves * numOfProductsDisplay;
            let l  = (numOfMoves * numOfProductsDisplay) + numOfProductsDisplay;
            let key = s.toString()+l.toString();
            if(alreadyShownSlices.includes(key)){
                return;
            }
            const shownProducts = productsList?.slice(s, l);
            let ids = shownProducts?.map(o => (o?.id));
            let srcs = shownProducts?.map(o => (o?.rec_src));
            $Reporting.Recommendations.reportProductsShown({
                id: getProductPageID($platform), // src product
                related_sources: JSON.stringify({products: ids, rec_sources: srcs}),
                found_related: ids?.length > 0 ? 1 : 0,
                gtag_product_items: createGTagEventObj({products: shownProducts, ids: ids, listTitle: title}),
                st: getUserSession() ?? undefined
            });
            alreadyShownSlices.push(key);
            ReportingProductsShownSlice.set(alreadyShownSlices);
        }
    }
    const handleNext = () => {
        let numOfProductsToMove = numOfProductsDisplay;
        if (currentDot + 1 === dotsLength) return;
        if (currentDot + 2 === dotsLength && (currentDot + 1) * numOfProductsDisplay < productsList.length) {
            numOfProductsToMove = productsList.length - (currentDot + 1) * numOfProductsDisplay;
        }

        layout === 'horizontal' ? transformPX -= (parseInt(imgSize) + 20) * numOfProductsToMove : // 20 is the margin of 10px on each side
            transformPX -= (parseInt(imgSize) + 93) * numOfProductsToMove // 93 is the margin of 10px on each side + content of the product
        numOfMoves += 1;
        productsViewedPostLoad();
    }

    const HandleBack = () => {
        let numOfProductsToMove = numOfProductsDisplay;
        if (transformPX === 0) return
        if (currentDot + 1 === dotsLength && currentDot * numOfProductsDisplay < productsList.length) {
            numOfProductsToMove = productsList.length - currentDot * numOfProductsDisplay;
        }
        layout === 'horizontal' ? transformPX += (parseInt(imgSize) + 20) * numOfProductsToMove :
            transformPX += (parseInt(imgSize) + 93) * numOfProductsToMove
        numOfMoves -= 1
    }

    const dotClick = (dot: number) => {
        let numOfProductsToMove: number;
        if (dot > currentDot) {
            numOfProductsToMove = numOfProductsDisplay * (dot - currentDot);
            if (dot === dotsLength - 1 && (dot + 1) * numOfProductsDisplay > productsList.length) {
                numOfProductsToMove = numOfProductsToMove + (productsList.length - (dot + 1) * numOfProductsDisplay);
            }
            layout === 'horizontal' ?
                transformPX -= (parseInt(imgSize) + 20) * numOfProductsToMove
                : transformPX -= (parseInt(imgSize) + 93) * numOfProductsToMove
        } else {
            numOfProductsToMove = numOfProductsDisplay * (currentDot - dot);
            if (currentDot === dotsLength - 1 && dotsLength * numOfProductsDisplay > productsList.length) {
                numOfProductsToMove = numOfProductsToMove + (productsList.length - dotsLength * numOfProductsDisplay);
            }
            layout === 'horizontal' ?
                transformPX += (parseInt(imgSize) + 20) * numOfProductsToMove
                : transformPX += (parseInt(imgSize) + 93) * numOfProductsToMove
        }
        numOfMoves = dot;
    }

    function swipeHandler(event) {
        const direction = event.detail.direction;
        if (direction === 'left') {
            handleNext()
        }
        if (direction === 'right') {
            HandleBack()
        }
    }

    useCustomCSS(target);

    /* get products and save in store */
    $: if ($previewMode) {
        setPreviewProducts(id);
    } else {
        setProducts(props, id, dev)
    }


    /* Reactive: widget product list */
    $: productsList = $products[id]

    /* Reactive: widget loading */
    $: if (Boolean(productsList)) {
        loading = false;
        productsViewedPostLoad();
    }

    /* Reactive: pagination dots length */
    $: if (!loading && productsList.length && productsList.length > 0) {
        dotsLength = Math.ceil(productsList.length / numOfProductsDisplay);
        currentDot = numOfMoves;
    }
    /* Reactive: products display */
    $: if ($widgetWidth && $widgetWidth <= 600) {
        numOfProductsDisplay = productsPerMobile;
    } else {
        numOfProductsDisplay = productsPerPage
    }

    /* Reactive: carousel width according to widget container width and device type */
    $:  if (currentDevice && currentDevice === "desktop") {
        (layout === "horizontal" ? carouselContainerWidth = $widgetWidth - 320 : carouselContainerHeight = $widgetHeight - 500)
    } else if (currentDevice && currentDevice === "tablet") {
        (layout === "horizontal" ? carouselContainerWidth = $widgetWidth - 200 : carouselContainerHeight = $widgetHeight)
    } else {
        (layout === "horizontal" ? carouselContainerWidth = $widgetWidth - 120 : carouselContainerHeight = $widgetHeight)
    }

    /* Reactive: image width according to carousel width and number of products to display */
    $: imgSize = fixedImageSize ? fixedImageSize : getImageSize(Number(numOfProductsDisplay), (layout === "horizontal" ? carouselContainerWidth : carouselContainerHeight))

    /* Reactive: determine carousel container according to layout */
    $: layout === 'horizontal' ? carouselContainerStyle = `max-width: ${numOfProductsDisplay * imgSize + numOfProductsDisplay * 20}px`
        : carouselContainerStyle = `max-height: ${numOfProductsDisplay * imgSize + numOfProductsDisplay * 93.3}px`

    /* Reactive: if any products has reviews */
    $: productsWithReviews = productsList && productsList.length > 0 ? (productsList.map(product => "review" in product)).includes(true) : false


    /* Reactive: create productDict for customization */
    let productDict: Record<string, ServerProduct> = {};
    $: productDict = productsList ? productsList.reduce((acc, product) => {
        acc[product.id] = product;
        return acc;
    }, {} as Record<string, ServerProduct>) : {}

    let carouselContainerClass: string
    $: carouselContainerClass = `carousel-container fs-recommendation-carousel-container-${id}`

</script>

<div use:containerWidth="{widgetWidth}" use:containerHeight="{widgetHeight}" id="fs-recommendation-widget-main">

    {#if loading}
        <Spinner color="black"/>
    {:else if productsList && productsList.length > 0}
        {#if title}
            <h2 class="fs-recommendation-widget-title" class:preview-title="{$previewMode === true}">{title}</h2>
        {/if}
        <div class="widget-container fs-recommendation-widget-container"
             use:swipe={{ timeframe: 300, minSwipeDistance: 10, touchAction: 'pan-y'}} on:swipe={swipeHandler}>
            <div class:products-container-horizontal="{layout === 'horizontal'}"
                 class:products-container-vertical="{layout === 'vertical'}"
                 class="fs-recommendation-widget-products-container">
                {#if dotsLength > 1}
                    <button class:arrow-vertical="{layout === 'vertical'}" on:click={HandleBack}
                        aria-label="Left Arrow" class="fs-recommendation-button">
                        <LeftArrowIcon/>
                    </button>
                {/if}
                <div class={carouselContainerClass} id="carousel"
                     style={carouselContainerStyle} class:preview-container="{$previewMode === true}">
                    <div class:slide-horizontal="{layout === 'horizontal'}" class="upsell-cross-sell-product-grid"
                         class:slide-vertical="{layout === 'vertical'}"
                         use:hook={{callback: $Hooks[CustomizationEvent.Products], params: {products: productDict}}}>
                        {#each productsList as product, i}
                            <Product name={product.l} currency={product.c} imgSize={imgSize} imgRatio={imgRatio}
                                     imageSrc={product.t ?? product.t2} id={product.id} title={title}
                                     productsWithReviews={productsWithReviews}
                                     price={product.p} vertical={layout === 'vertical'} minPrice={product.p_min}
                                     previousPrice={product.p_c} i={i} productsPerPage={numOfProductsDisplay}
                                     review={product.review} transformPX={transformPX} onHoverCarousel={onHoverCarousel}
                                     reviews_count={product.reviews_count} path={product.u} addToCart={addToCart} product={product} recSource={product?.rec_src}/>
                        {/each}
                    </div>
                </div>
                {#if dotsLength > 1}
                    <button class:arrow-vertical="{layout === 'vertical'}" on:click={handleNext}
                            aria-label="Right Arrow" class="fs-recommendation-button">
                        <RightArrowIcon/>
                    </button>
                {/if}
            </div>
        </div>
        <div>
            {#if dotsLength > 1}
                <DotsContainer dotsLength={dotsLength} currentDot={currentDot} onDotClick={dotClick}/>
            {/if}
        </div>
    {:else if dev && productsList.length === 0}
        Server returned 0 products.
    {/if}
</div>

<style>

    #fs-recommendation-widget-main {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .widget-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    .carousel-container {
        width: 100%;
        height: 100%;
        padding: 10px;
        position: relative;
        display: flex;
        flex-direction: column;
        overflow-x: hidden;
        overflow-y: hidden;
    }

    .products-container-horizontal {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: nowrap;
    }

    .products-container-vertical {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex-wrap: nowrap;
    }

    .preview-title {
        margin-bottom: 0;
    }

    .preview-container {
        pointer-events: none;
    }

    button {
        background: none;
        border: none;
        outline: none;
        cursor: pointer;
        font-size: 2em;
        color: #000;
        font-weight: bold;
    }

    @media (max-width: 480px) {
        button {
            font-size: 1em;
        }
    }

    .arrow-vertical {
        transform: rotate(90deg);
    }

    .slide-horizontal {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: nowrap;
    }

    .slide-vertical {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: nowrap;
    }
    .empty-test-class{}
    .empty-test-class-none{display: none}
</style>
